import type { Breadcrumb, Event } from '@sentry/core'
import { redactPhiInPlainText, scrubSentryContexts, scrubSentryExtra } from '@/shared/lib/sentryPhiScrub'
import { sanitizeSensitiveUrl } from '@/shared/lib/sanitizeSensitiveUrl'

const MAX_EXCEPTION_MSG = 2000

export function redactPlainText(value: string): string {
  return redactPhiInPlainText(value, MAX_EXCEPTION_MSG)
}

function redactUrl(raw: string): string {
  const base = typeof window !== 'undefined' ? window.location.origin : 'https://local.invalid'
  try {
    const absolute = new URL(raw, base).toString()
    return sanitizeSensitiveUrl(absolute)
  } catch {
    return sanitizeSensitiveUrl(raw)
  }
}

function sanitizeBreadcrumbData(data: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!data || typeof data !== 'object') return data
  const next: Record<string, unknown> = { ...data }
  if (typeof next.url === 'string') next.url = redactUrl(next.url)
  if (typeof next.from === 'string') next.from = redactUrl(next.from)
  if (typeof next.to === 'string') next.to = redactUrl(next.to)
  delete next.request_body
  delete next.response_body
  delete next.input
  delete next.output
  return next
}

export function sanitizeBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  const category = breadcrumb.category ?? ''
  if (category === 'fetch' || category === 'xhr' || category === 'navigation') {
    return {
      ...breadcrumb,
      message: breadcrumb.message ? redactPlainText(breadcrumb.message) : breadcrumb.message,
      data: sanitizeBreadcrumbData(breadcrumb.data as Record<string, unknown> | undefined),
    }
  }
  return breadcrumb
}

function sanitizeExceptionValues(event: Event): void {
  const values = event.exception?.values
  if (!values) return
  for (const ex of values) {
    if (ex.value) ex.value = redactPlainText(ex.value)
    if (ex.stacktrace?.frames) {
      for (const frame of ex.stacktrace.frames) {
        if (frame.vars && typeof frame.vars === 'object') {
          frame.vars = undefined
        }
      }
    }
  }
}

/**
 * Minimiza vazamento de dados clínicos/PII e segredos em payloads Sentry.
 */
export function sanitizeSentryEvent(event: Event): Event {
  if (event.message) event.message = redactPlainText(event.message)
  if (event.logentry?.message) {
    event.logentry.message = redactPlainText(event.logentry.message)
  }

  if (event.user && typeof event.user === 'object') {
    const id = event.user.id
    event.user = id ? { id: String(id) } : {}
  }

  if (event.request?.url) {
    event.request.url = redactUrl(String(event.request.url))
  }
  if (event.request?.query_string) {
    event.request.query_string = '[REDACTED]'
  }
  if (event.request?.data) {
    event.request.data = undefined
  }
  if (event.request?.cookies) {
    event.request.cookies = undefined
  }

  if (event.breadcrumbs?.length) {
    event.breadcrumbs = event.breadcrumbs.map((b) => sanitizeBreadcrumb(b))
  }

  sanitizeExceptionValues(event)

  if (event.extra) {
    event.extra = scrubSentryExtra(event.extra as Record<string, unknown>)
  }
  if (event.contexts) {
    event.contexts = scrubSentryContexts(event.contexts as Record<string, Record<string, unknown>>)
  }

  return event
}
